import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ApiError, api } from '../../lib/api';
import { Modal } from '../albums/Modal';
import { Artwork } from '../dashboard/Artwork';
import { useArtistTracks } from '../tracks/useArtistTracks';

/**
 * Putting a different track in a slot you already hold.
 *
 * ## Why the slot is worth keeping
 *
 * Withdrawing and submitting again loses the place in the queue, and if the
 * submission was boosted it loses the money too. Somebody who picked the wrong
 * track should not have to pay twice for the mistake - so the row survives and
 * only what it points at changes.
 *
 * ## The room still gets its say
 *
 * `SubmissionAction` re-runs every rule submission applies - the playable
 * guard, the room's explicit line, one live copy of a track per room - so this
 * is a new submission in an old slot rather than a way round the door policy.
 * The refusals arrive as sentences and are shown as sentences.
 */

export function SwapDialog({
  queueId,
  username,
  currentTrackId,
  onClose,
}: {
  queueId: number;
  /** Whose tracks to offer - the person doing the swapping. */
  username: string;
  currentTrackId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { items } = useArtistTracks(username, { all: true });

  const swap = useMutation({
    mutationFn: (trackId: number) => api.patch(`/me/queue/${queueId}`, { track_id: trackId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me', 'queue'] });
      onClose();
    },
    onError: (caught: unknown) => {
      /*
       * The server's own sentence. Every refusal here is something the person
       * can act on - a room that takes no explicit tracks, a track already in
       * that queue - and replacing it with "that did not work" would send them
       * back to try the same one.
       */
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    },
  });

  // Still encoding is not playable, and the one already in the slot is not a
  // swap. Both are absent rather than disabled: a list of things you cannot
  // choose is a longer list to read past.
  const choices = items.filter(
    (track) => track.status === 'ready' && track.id !== currentTrackId,
  );

  return (
    <Modal title="Swap this submission" onClose={onClose}>
      <div className="flex flex-col gap-4 p-5" data-swap-dialog>
        <p className="text-sm text-soft">
          The place in the queue stays where it is. Only the track changes.
        </p>

        {error && <Alert tone="error">{error}</Alert>}

        {choices.length === 0 ? (
          <p className="font-mono text-[11px] text-soft">
            Nothing else of yours is ready to play yet.
          </p>
        ) : (
          <ul className="flex max-h-80 flex-col gap-1.5 overflow-y-auto">
            {choices.map((track) => (
              <li key={track.id}>
                <button
                  type="button"
                  data-swap-to={track.id}
                  disabled={swap.isPending}
                  onClick={() => swap.mutate(track.id)}
                  className="flex w-full items-center gap-3 rounded-[var(--radius-control)] p-2 text-left transition-colors hover:bg-raised disabled:opacity-60"
                >
                  <Artwork
                    track={{ title: track.title, artwork: null, artwork_small: track.artwork_small }}
                    className="h-9 w-9 shrink-0 rounded-[var(--radius-chip)]"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{track.title}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end">
          <Button variant="ghost" onClick={onClose} disabled={swap.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
